import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/utils/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Edit, Search, Calendar, Heart, Activity, Eye, FileText } from "lucide-react";
import { toast } from "sonner";

const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const backendBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return `${backendBase}${url.startsWith('/') ? '' : '/'}${url}`;
};

const StudentManagement = ({ permissions }) => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editData, setEditData] = useState({
    room_number: "",
    interests: "",
    known_diseases: "",
    secondary_phone: "",
    parent_email: "",
    local_guardian_name: "",
    local_guardian_phone: ""
  });

  const canEdit = permissions?.can_update_student_details || false;
  const canEditParentContact = permissions?.can_update_parent_contact || false;

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await apiClient.get('/v1/warden/students/my-students');
      setStudents(response.data.students || []);
    } catch (error) {
      toast.error("Failed to fetch students");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (student) => {
    if (!canEdit && !canEditParentContact) {
      toast.error("You don't have permission to edit student details");
      return;
    }
    
    setSelectedStudent(student);
    setEditData({
      room_number: student.room_number || "",
      interests: Array.isArray(student.interests) ? student.interests.join(", ") : "",
      known_diseases: student.known_diseases || "",
      secondary_phone: student.secondary_phone || "",
      parent_email: student.parent_email || "",
      local_guardian_name: student.local_guardian_name || "",
      local_guardian_phone: student.local_guardian_phone || ""
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      
      // Only include fields based on permissions
      if (canEdit) {
        payload.room_number = editData.room_number;
        payload.interests = editData.interests.split(",").map(i => i.trim()).filter(Boolean);
        payload.known_diseases = editData.known_diseases;
      }
      
      if (canEditParentContact) {
        payload.secondary_phone = editData.secondary_phone;
        payload.parent_email = editData.parent_email;
        payload.local_guardian_name = editData.local_guardian_name;
        payload.local_guardian_phone = editData.local_guardian_phone;
      }
      
      await apiClient.put(`/v1/warden/students/${selectedStudent.id}`, payload);
      toast.success("Student updated successfully!");
      setEditDialogOpen(false);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update student");
    }
  };

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.class_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.room_number.includes(searchTerm)
  );

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="loading-spinner"></div></div>;
  }

  return (
    <div data-testid="student-management" className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>Student Management</h1>
        <div className="text-sm text-gray-600">
          <p>View and Update Student Records</p>
          <p className="text-xs">Contact admin to add/remove students</p>
        </div>
      </div>

      <div className="glass-effect p-4 rounded-lg">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="search-students-input"
            placeholder="Search students by name, class, or room..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStudents.map((student) => (
          <Card key={student.id} className="glass-effect card-hover" data-testid={`student-card-${student.id}`}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-12 h-12 border-2 border-blue-100 shadow-2xs">
                    <AvatarImage src={getPhotoUrl(student.photo_url)} alt={student.name} className="object-cover" />
                    <AvatarFallback className="bg-blue-100 text-xl font-bold" style={{ color: "#7CB9E8" }}>
                      {student.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold" style={{ color: "#2C5F7F" }}>{student.name}</h3>
                    <p className="text-sm text-gray-600">Age: {student.age}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Class:</span> {student.class_name}</p>
                <p><span className="font-medium">Room:</span> {student.room_number || "Not assigned"}</p>
                <p><span className="font-medium">Blood Group:</span> {student.blood_group || "N/A"}</p>
                {student.interests && student.interests.length > 0 && (
                  <p><span className="font-medium">Interests:</span> {student.interests.join(", ")}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {(canEdit || canEditParentContact) && (
                  <Button 
                    data-testid={`edit-student-${student.id}`} 
                    size="sm" 
                    variant="outline"
                    onClick={() => openEditDialog(student)}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => navigate(`/warden/student-profile/${student.id}`)}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  View
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredStudents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No students found</p>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <Avatar className="w-12 h-12 border border-blue-100">
                <AvatarImage src={getPhotoUrl(selectedStudent?.photo_url)} alt={selectedStudent?.name} className="object-cover" />
                <AvatarFallback className="bg-blue-100 text-lg font-bold" style={{ color: "#7CB9E8" }}>
                  {selectedStudent?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <DialogTitle>Update Student Details</DialogTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedStudent?.name} - {selectedStudent?.class_name}
                </p>
              </div>
            </div>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            {canEdit && (
              <>
                <div className="border-b pb-3">
                  <h3 className="font-semibold mb-3">Student Information</h3>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="edit-room">Room Number</Label>
                      <Input
                        id="edit-room"
                        data-testid="edit-student-room-input"
                        value={editData.room_number}
                        onChange={(e) => setEditData({ ...editData, room_number: e.target.value })}
                        placeholder="e.g., 101"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-interests">Interests (comma separated)</Label>
                      <Input
                        id="edit-interests"
                        data-testid="edit-student-interests-input"
                        value={editData.interests}
                        onChange={(e) => setEditData({ ...editData, interests: e.target.value })}
                        placeholder="Sports, Art, Music"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-diseases">Known Diseases</Label>
                      <Input
                        id="edit-diseases"
                        value={editData.known_diseases}
                        onChange={(e) => setEditData({ ...editData, known_diseases: e.target.value })}
                        placeholder="None or specify"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {canEditParentContact && (
              <>
                <div className="border-b pb-3">
                  <h3 className="font-semibold mb-3">Parent/Guardian Contact</h3>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="edit-secondary-phone">Secondary Phone</Label>
                      <Input
                        id="edit-secondary-phone"
                        type="tel"
                        value={editData.secondary_phone}
                        onChange={(e) => setEditData({ ...editData, secondary_phone: e.target.value })}
                        placeholder="+91XXXXXXXXXX"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-email">Parent Email</Label>
                      <Input
                        id="edit-email"
                        type="email"
                        value={editData.parent_email}
                        onChange={(e) => setEditData({ ...editData, parent_email: e.target.value })}
                        placeholder="parent@example.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-guardian-name">Local Guardian Name</Label>
                      <Input
                        id="edit-guardian-name"
                        value={editData.local_guardian_name}
                        onChange={(e) => setEditData({ ...editData, local_guardian_name: e.target.value })}
                        placeholder="Guardian name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-guardian-phone">Local Guardian Phone</Label>
                      <Input
                        id="edit-guardian-phone"
                        type="tel"
                        value={editData.local_guardian_phone}
                        onChange={(e) => setEditData({ ...editData, local_guardian_phone: e.target.value })}
                        placeholder="+91XXXXXXXXXX"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {!canEdit && !canEditParentContact && (
              <p className="text-center text-gray-500 py-4">
                You don't have permission to edit any fields
              </p>
            )}
            
            <Button 
              data-testid="submit-update-btn" 
              type="submit" 
              className="w-full btn-primary text-white"
              disabled={!canEdit && !canEditParentContact}
            >
              Update Student
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentManagement;